import React, { useCallback, useMemo } from "react";

import { useTranslation } from "utils";

import { COUNTRY_CODE } from "consts";

import { CropsWithMissingHistoricalPracticeDataByYear } from "models";

import { Button, Flex, WidgetVideo, Message as message } from "components";

import { HISTORICAL_PRACTICE_EXPLAINER_VIDEO_VIMEO_URL } from "pages/app/historical-practice-data-table-view/HistoricalPracticeDataTableView";
import { FileUploadTile } from "pages/app/historical-practice-data-table-view/components/FileUploadTile";
import { SidePanelLoadingSkeleton } from "pages/app/historical-practice-data-table-view/components/LoadingSkeletons";
import { ProgressTile } from "pages/app/historical-practice-data-table-view/components/ProgressTile";
import { isYearComplete } from "pages/app/historical-practice-data-table-view/utils/HistoricalDataUtils";

const PROGRESS_COLOR = "#065C45";
const TRAIL_COLOR = "#B4CEC7";

export const SidePanel = ({
  cropsWithMissingHistoricalPracticeDataByYear,
  availableYears,
  selectedYear,
  setSelectedYear,
  isLoading,
  fileUploadTaskProgress,
  isFileUploadDrawerOpen,
  onFileUploadDrawerOpenChange,
  onSaveAndExit,
}: {
  cropsWithMissingHistoricalPracticeDataByYear: CropsWithMissingHistoricalPracticeDataByYear;
  availableYears: number[];
  selectedYear: number | undefined;
  setSelectedYear: (year: number) => void;
  isLoading: boolean;
  fileUploadTaskProgress: { total: number; completed: number };
  isFileUploadDrawerOpen: boolean;
  onFileUploadDrawerOpenChange: (open: boolean) => void;
  onSaveAndExit: () => void;
}) => {
  const { t } = useTranslation("historicalPracticeDataTableView");

  const { earliestYearWithMissingData, yearHasMissingData } = useMemo(() => {
    const hasMissingData = (year: number) => !isYearComplete(year, cropsWithMissingHistoricalPracticeDataByYear);

    return {
      earliestYearWithMissingData: availableYears.find(hasMissingData),
      yearHasMissingData: hasMissingData,
    };
  }, [cropsWithMissingHistoricalPracticeDataByYear, availableYears]);

  const handleCompleteTask = useCallback(() => {
    if (earliestYearWithMissingData !== undefined) {
      message({ content: t("sidePanel.messages.failedToCompleteTask"), icon: "error" });
      const currentYearHasMissingData = selectedYear !== undefined && yearHasMissingData(selectedYear);
      if (!currentYearHasMissingData) {
        setSelectedYear(earliestYearWithMissingData);
      }
      return;
    }

    onSaveAndExit();
  }, [earliestYearWithMissingData, selectedYear, yearHasMissingData, setSelectedYear, onSaveAndExit, t]);

  return (
    <Flex className="historical-practice-data-table-view-side-panel-container" vertical gap="1rem">
      {isLoading ? (
        <SidePanelLoadingSkeleton />
      ) : (
        <>
          <ProgressTile
            cropsWithMissingHistoricalPracticeDataByYear={cropsWithMissingHistoricalPracticeDataByYear}
            availableYears={availableYears}
            progressBarColors={{ progressColor: PROGRESS_COLOR, trailColor: TRAIL_COLOR }}
          />
          {fileUploadTaskProgress.total > 0 && (
            <FileUploadTile
              openFileUploadTasksCount={fileUploadTaskProgress}
              progressBarColors={{ progressColor: PROGRESS_COLOR, trailColor: TRAIL_COLOR }}
              isDrawerOpen={isFileUploadDrawerOpen}
              onDrawerOpenChange={onFileUploadDrawerOpenChange}
            />
          )}
          <WidgetVideo
            vimeoUrlOrId={HISTORICAL_PRACTICE_EXPLAINER_VIDEO_VIMEO_URL}
            title={t("sidePanel.explainer.title")}
            description={t("sidePanel.explainer.description")}
            allowedCountries={[COUNTRY_CODE.GERMANY]}
          />
          <Flex vertical gap="0.75rem" className="margin-top-100">
            <Button type="primary" onClick={handleCompleteTask}>
              {t("sidePanel.actions.completeTask")}
            </Button>
            <Button type="secondary" onClick={onSaveAndExit}>
              {t("sidePanel.actions.saveAndGoBack")}
            </Button>
          </Flex>
        </>
      )}
    </Flex>
  );
};
